import { Routes } from '@angular/router';

import { OperatorDashboardComponent } from './pages/dashboard/operator-dashboard/operator-dashboard.component';
import { ApplyComponent } from './pages/onboarding/apply/apply.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { NotFoundComponent } from './pages/other-page/not-found/not-found.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { AppLayoutComponent } from './shared/layout/app-layout/app-layout.component';
import { SignInComponent } from './pages/auth-pages/sign-in/sign-in.component';
import { SignUpComponent } from './pages/auth-pages/sign-up/sign-up.component';

import { authGuard } from './shared/guards/auth.guard';
import { operatorAreaGuard } from './shared/guards/operator-area.guard';

/**
 * Operator app : le sous-arbre `AppLayoutComponent` est gardé par
 * `operatorAreaGuard` — il exige le rôle OPERATOR, sinon il **redirige vers
 * `/apply`** (et non `/forbidden`) : le rôle OPERATOR n'est assigné qu'à
 * l'approbation admin, donc un user authentifié sans rôle est un candidat
 * (ou un opérateur PENDING) qu'on oriente vers la candidature / l'écran d'attente.
 *
 * `/apply` (candidature) est accessible à tout user authentifié — sous
 * `authGuard` seul, hors `operatorAreaGuard`.
 *
 * NOTE : shell en construction — dashboard réel (#68) + candidature (#67) +
 * notifications + profil. Modules operator restants (prestataires, wallet,
 * kyc, licence/zone) ajoutés au fil des prochains commits.
 */
export const routes: Routes = [
  // ── Candidature — authentifié, pas (encore) opérateur ──────────────────────
  { path: 'apply', component: ApplyComponent, canActivate: [authGuard], title: 'Nawam Operator — Candidature' },

  // Sous-arbre principal — rôle OPERATOR requis (sinon → /apply).
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [operatorAreaGuard],
    children: [
      { path: '', pathMatch: 'full', component: OperatorDashboardComponent, title: 'Nawam Operator — Tableau de bord' },

      // ── Notifications ─────────────────────────────────────────────────
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications-list/notifications-list.component').then(
            (m) => m.NotificationsListComponent,
          ),
        title: 'Nawam Operator — Notifications',
      },

      // ── Mes prestataires ────────────────────────────────────────────────
      {
        path: 'prestataires',
        loadComponent: () =>
          import('./pages/prestataires/prestataires-list/prestataires-list.component').then(
            (m) => m.PrestatairesListComponent,
          ),
        title: 'Nawam Operator — Mes prestataires',
      },

      // ── Mon wallet ──────────────────────────────────────────────────────
      {
        path: 'wallet',
        loadComponent: () =>
          import('./pages/wallet/wallet.component').then((m) => m.OperatorWalletComponent),
        title: 'Nawam Operator — Mon wallet',
      },

      // ── Retraits BALANCE (#57) ──────────────────────────────────────────
      {
        path: 'withdrawals',
        loadComponent: () =>
          import('./pages/withdrawals/withdrawals-list/withdrawals-list.component').then(
            (m) => m.WithdrawalsListComponent,
          ),
        title: 'Nawam Operator — Retraits',
      },

      // ── Ma licence ──────────────────────────────────────────────────────
      {
        path: 'license',
        loadComponent: () =>
          import('./pages/license/license.component').then((m) => m.OperatorLicenseComponent),
        title: 'Nawam Operator — Ma licence',
      },

      // ── Réconciliation cash hebdomadaire ────────────────────────────────
      {
        path: 'cash-reconciliation',
        loadComponent: () =>
          import('./pages/cash-reconciliation/cash-reconciliation.component').then(
            (m) => m.CashReconciliationComponent,
          ),
        title: 'Nawam Operator — Réconciliation',
      },

      // ── Mon dossier KYC (prérequis au recrutement, #66) ─────────────────
      {
        path: 'kyc',
        loadComponent: () =>
          import('./pages/kyc/kyc.component').then((m) => m.OperatorKycComponent),
        title: 'Nawam Operator — Mon dossier KYC',
      },

      // ── Atterrissage post-paiement (dispatcher payment-service) ─────────
      // Le back redirige vers /payment/result/<returnPath>?txId=…&status=…
      {
        path: 'payment/result/**',
        loadComponent: () =>
          import('./pages/payment/payment-result/payment-result.component').then(
            (m) => m.PaymentResultComponent,
          ),
        title: 'Nawam Operator — Paiement',
      },

      // ── Profil ────────────────────────────────────────────────────────
      { path: 'profile', component: ProfileComponent, title: 'Nawam Operator — Profil' },
    ],
  },

  // ── public ────────────────────────────────────────────────────────────────
  { path: 'signin', component: SignInComponent, title: 'Nawam Operator — Connexion' },
  { path: 'signup', component: SignUpComponent, title: 'Nawam Operator — Inscription' },
  {
    path: 'auth/set-password/:token',
    loadComponent: () =>
      import('./pages/auth-pages/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
    title: 'Nawam Operator — Définir mon mot de passe',
  },
  {
    path: 'forgotten-password',
    loadComponent: () =>
      import('./pages/auth-pages/forgotten-password/forgotten-password.component').then(
        (m) => m.ForgottenPasswordComponent,
      ),
    title: 'Nawam Operator — Mot de passe oublié',
  },
  { path: 'forbidden', component: ForbiddenComponent, title: 'Nawam Operator — Accès refusé' },

  // ── catch-all ────────────────────────────────────────────────────────────
  { path: '**', component: NotFoundComponent, title: 'Nawam Operator — Page introuvable' },
];
